import {
	MatchingBracketDataI,
	MatchingTierConfigI
} from '../api/companies/interfaces/CompanyEppConfig';

/**
 * Frontend mirror of comparsa's resolve-matching-contribution util.
 * Keep both in sync: comparsa/src/companies/utils/resolve-matching-contribution.ts
 */

export interface ResolveMatchingContributionParams {
	matchingBracket: MatchingBracketDataI;
	employeeCategory?: string;
	/**
	 * Monthly employee contribution in € (already converted from FIXED/PERCENTAGE)
	 */
	employeeMonthlyContribution: number;
}

/**
 * Resolves the matching tier for a user: byCategory[employeeCategory] ?? default ?? null
 */
export function resolveMatchingTier(
	matchingBracket: MatchingBracketDataI,
	employeeCategory?: string
): MatchingTierConfigI | null {
	const categoryTier = employeeCategory
		? matchingBracket.byCategory?.[employeeCategory]
		: undefined;
	return categoryTier ?? matchingBracket.default ?? null;
}

/**
 * MATCHING mode: the employer matches (iguala) the employee contribution.
 * - No resolvable tier → 0€
 * - employeeMonthlyContribution <= 0 → 0€
 * - employeeMonthlyContribution < tier.minEmployeeContribution → 0€ (activation threshold)
 * - Otherwise → min(employeeMonthlyContribution, tier.maxAmount ?? Infinity)
 * Rounded to 2 decimals.
 */
export function resolveMatchingContribution(params: ResolveMatchingContributionParams): number {
	const { matchingBracket, employeeCategory, employeeMonthlyContribution } = params;

	const tier = resolveMatchingTier(matchingBracket, employeeCategory);
	if (!tier) {
		return 0;
	}

	if (employeeMonthlyContribution <= 0) {
		return 0;
	}

	if (
		tier.minEmployeeContribution !== undefined &&
		tier.minEmployeeContribution !== null &&
		employeeMonthlyContribution < tier.minEmployeeContribution
	) {
		return 0;
	}

	const matched = Math.min(employeeMonthlyContribution, tier.maxAmount ?? Infinity);

	return Math.round(matched * 100) / 100;
}
